import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { tapHaptic } from "../lib/haptics";

/**
 * LiveChatOverlay — TikTok Live-style ephemeral chat feed. Each new
 * message pops up from the bottom-left, floats upward briefly, then
 * fades out. Long-press a message → reaction picker (👍 ❤️ 🔥 😂 😢 👏 🙌).
 *
 * Sources:
 *  - `messages` prop: array of chat rows already fetched by the parent
 *    (from `/live/{id}/chat` REST or the live-chat WebSocket).
 *  - The parent is responsible for pushing new messages into `messages`.
 *    We de-dupe by `id` here.
 *
 * Backend calls we own:
 *  - `POST /api/live/{stream_id}/chat/{message_id}/react` — increments
 *    the emoji count on the chat doc.
 */

const REACTIONS = ["👍", "❤️", "🔥", "😂", "😢", "👏", "🙌"];
const VISIBLE_MS = 6000;   // time a message stays fully visible
const FADE_MS = 900;       // css fade duration
const MAX_ON_SCREEN = 5;   // avoid clutter

function fmtName(m) {
  return m?.user_name || m?.name || "Viewer";
}

function ChatBubble({ msg, onLongPress }) {
  const timerRef = useRef(null);
  const armedRef = useRef(false);
  const [reactions, setReactions] = useState(msg.reactions || {});

  const startPress = (e) => {
    armedRef.current = true;
    timerRef.current = setTimeout(() => {
      if (armedRef.current) {
        try { tapHaptic(); } catch { /* ignore */ }
        onLongPress?.(msg, (emoji) => {
          setReactions((prev) => ({ ...prev, [emoji]: (prev[emoji] || 0) + 1 }));
        });
      }
    }, 450);
    e.stopPropagation();
  };
  const cancelPress = () => {
    armedRef.current = false;
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  const reactionEntries = Object.entries(reactions).filter(([, n]) => n > 0);

  return (
    <div
      data-testid="live-chat-bubble"
      onPointerDown={startPress}
      onPointerUp={cancelPress}
      onPointerLeave={cancelPress}
      onPointerCancel={cancelPress}
      className="pointer-events-auto max-w-[75%] rounded-2xl bg-black/55 backdrop-blur px-3 py-1.5 text-white shadow-lg select-none"
    >
      <div className="flex items-start gap-2">
        <img
          src={msg.user_avatar || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(fmtName(msg))}`}
          alt=""
          className="h-6 w-6 rounded-full border border-white/60 mt-0.5 object-cover bg-neutral-800 flex-shrink-0"
          loading="lazy"
        />
        <div className="min-w-0">
          <div className="text-[11px] font-black leading-tight text-yellow-300/95">@{fmtName(msg)}</div>
          <div className="text-[13px] leading-tight break-words">{msg.message || msg.body || ""}</div>
        </div>
      </div>
      {reactionEntries.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1" data-testid="live-chat-reactions">
          {reactionEntries.map(([e, n]) => (
            <span key={e} className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-white/10 rounded-full px-1.5 py-0.5">
              <span>{e}</span>
              <span>{n}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ReactionPicker({ msg, onPick, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[75] flex items-center justify-center"
      data-testid="live-chat-reaction-picker"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative bg-neutral-900 rounded-2xl px-3 py-2 border border-white/10 shadow-2xl">
        <div className="text-white/60 text-[10px] uppercase tracking-widest mb-1.5 px-1">
          React to @{fmtName(msg)}
        </div>
        <div className="flex items-center gap-1">
          {REACTIONS.map((r) => (
            <button
              key={r}
              data-testid={`live-chat-react-${r}`}
              onClick={(e) => { e.stopPropagation(); onPick(r); }}
              className="h-11 w-11 rounded-xl bg-white/5 hover:bg-white/15 active:scale-95 transition-transform text-2xl flex items-center justify-center"
            >
              {r}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function LiveChatOverlay({ streamId, messages, isAuthed = false, className = "" }) {
  // Ephemeral list — each entry is { msg, addedAt } with a client-side
  // TTL of VISIBLE_MS. We de-dupe by msg.id so re-sends from the parent
  // don't stack duplicates.
  const [live, setLive] = useState([]);
  const seenRef = useRef(new Set());
  const [picker, setPicker] = useState(null);

  // Ingest new messages from the parent's prop.
  useEffect(() => {
    if (!Array.isArray(messages) || messages.length === 0) return;
    const fresh = [];
    for (const m of messages) {
      const key = m?.id || `${m?.user_id}-${m?.created_at}`;
      if (!key || seenRef.current.has(key)) continue;
      seenRef.current.add(key);
      fresh.push({ msg: m, addedAt: Date.now(), key });
    }
    if (fresh.length === 0) return;
    setLive((cur) => {
      const merged = [...cur, ...fresh];
      // Keep only the last MAX_ON_SCREEN.
      return merged.slice(-MAX_ON_SCREEN);
    });
    // Schedule dismissal.
    fresh.forEach(({ key }) => {
      setTimeout(() => {
        setLive((cur) => cur.filter((e) => e.key !== key));
      }, VISIBLE_MS + FADE_MS);
    });
  }, [messages]);

  const handleLongPress = useCallback((msg, onLocalIncrement) => {
    if (!isAuthed) {
      toast.error("Sign in to react");
      return;
    }
    if (!msg?.id) return;
    setPicker({ msg, onLocalIncrement });
  }, [isAuthed]);

  const pick = useCallback(async (emoji) => {
    const p = picker;
    setPicker(null);
    if (!p?.msg?.id) return;
    // Optimistic UI — bump the count locally right away.
    try { p.onLocalIncrement?.(emoji); } catch { /* fine */ }
    try {
      await api.post(`/live/${streamId}/chat/${p.msg.id}/react`, { emoji });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't send reaction";
      toast.error(msg);
    }
  }, [picker, streamId]);

  if (!streamId) return null;

  return (
    <div
      className={`pointer-events-none absolute inset-x-3 bottom-24 flex flex-col justify-end items-start gap-1.5 max-h-[45vh] ${className}`}
      data-testid="live-chat-overlay"
    >
      {live.map(({ msg, key, addedAt }) => {
        const age = Date.now() - addedAt;
        const fading = age > VISIBLE_MS;
        return (
          <div
            key={key}
            style={{
              opacity: fading ? 0 : 1,
              transform: `translateY(${fading ? -8 : 0}px)`,
              transition: `opacity ${FADE_MS}ms ease-out, transform ${FADE_MS}ms ease-out`,
            }}
          >
            <ChatBubble msg={msg} onLongPress={handleLongPress} />
          </div>
        );
      })}
      {picker && (
        <ReactionPicker
          msg={picker.msg}
          onClose={() => setPicker(null)}
          onPick={pick}
        />
      )}
    </div>
  );
}
